<template>
    <div class="integration-details">
        <div class="a-container top-row">
            <a-link
                class="back-link"
                v-bind="backLink"
                :glyph="backLink.glyph || 'arrow-left'"
                :glyph-position="backLink.glyphPosition || 'left'"
            />
            <a-link class="support-link" v-bind="supportLink" />
        </div>
        <div class="main">
            <s-integration-header v-bind="header" />
            <s-integration-summary v-if="summary.title || summary.lead || summary.links.length" v-bind="summary" />
            <s-integration-features v-if="features.slides.length" v-bind="features" />
            <s-integration-body v-bind="body" />
            <s-integration-company v-bind="company" />
        </div>
    </div>
</template>

<script>
import ALink from '@core/components/link/link.vue';
import SIntegrationBody from './components/integration-body.vue';
import SIntegrationCompany from './components/integration-company.vue';
import SIntegrationFeatures from './components/integration-features.vue';
import SIntegrationHeader from './components/integration-header.vue';
import SIntegrationSummary from './components/integration-summary.vue';

export default {
    name: 'SolutionsIntegrationDetails',

    components: {
        SIntegrationHeader,
        SIntegrationSummary,
        SIntegrationBody,
        SIntegrationCompany,
        SIntegrationFeatures,
        ALink,
    },

    props: {
        header: {
            type: Object,
            required: true,
        },

        summary: {
            type: Object,
            required: true,
        },

        body: {
            type: Object,
            default: null,
        },

        features: {
            type: Object,
            required: true,
        },

        company: {
            type: Object,
            required: true,
        },

        backLink: {
            type: Object,
            required: true,
        },

        supportLink: {
            type: Object,
            default: null,
        },
    },
};
</script>

<style lang="postcss" scoped>
.integration-details {
    color: var(--av-fixed-primary);
}

.top-row {
    @mixin body-accent;

    display: flex;
    justify-content: space-between;
    flex-direction: column;
    gap: 16px;
    padding-top: 24px;

    @media (--viewport-mobile-wide) {
        flex-direction: row;
    }
}

.main {
    background: var(--av-brand-lightest);
    padding-bottom: 48px;
}

.main:deep(.a-dangerous-html) {
    h1, h2, h3, h4, h5, h6 {
        margin-bottom: 16px;
    }

    h3, h4, h5, h6 {
        margin-top: 16px;
    }

    h1 {
        /* NB: H1 discouraged, there must be only one H1 on the page */
        @mixin display;
    }

    h2 {
        @mixin title-accent;
    }

    h3 {
        @mixin lead-strong;
    }

    h4 {
        @mixin lead-accent;
    }

    h5 {
        @mixin lead;
    }

    h6 {
        @mixin paragraph-accent;
    }

    blockquote {
        margin: 16px 0;
        padding: 8px 16px;
        border-left: 4px solid var(--av-brand-accent);
    }

    hr {
        margin: 16px 0;
        border: none;
        border-top: 1px solid var(--av-brand-accent);
    }

    ul {
        margin: 16px 0;

        li {
            margin: 0px;
        }
    }

    ul ul, ul ol, ol ul, ol ol {
        margin-top: 0px;
        margin-left: 18px;
    }

    /* TODO: nested lists support should probably be a part of dangerous-html */
    ol {
        margin: 16px 0;
        list-style-type: decimal;
        list-style-position: inside;

        li {
            margin: 0px;
        }
    }

    table {
        border-collapse: collapse;
        margin: 24px 0;
    }

    th, td {
        border: 1px solid var(--av-brand-accent);
        padding: 8px;
        text-align: left;
    }

    th {
        font-weight: bold;
    }

    code {
        font-family: 'Fira Mono', Consolas, Monaco, monospace;
        font-size: 95%;
    }

    :not(pre) > code {
        background-color: var(--av-brand-accent);
        padding: 2px 4px;
    }

    pre {
        background-color: var(--av-brand-accent);
        padding: 2px 4px;
    }
}
</style>
